import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import moment from 'moment-timezone'
import AlertIcon from 'transfix-icons/build/AlertIcon'
import EditIcon from 'transfix-icons/build/EditIcon'
import SuccessIcon from 'transfix-icons/build/SuccessIcon'
import { Card } from 'transfix-ui/components/Card'
import { IconButton } from 'transfix-ui/components/IconButton'
import { InlineMessage, INLINE_MESSAGE_VARIANTS } from 'transfix-ui/components/InlineMessage'
import { Spinner } from 'transfix-ui/components/Spinner'
import { Typography } from 'transfix-ui/core'
import { ICarrierState, ICommodityState } from 'types/carrier'
import { getCarrierCommodities, showCommoditiesDialog } from 'utils/actions/carrier'
import { IStore } from 'apps/carrier/utils/reducers'
import { FORMATS } from 'constants/datetime'
import CommoditiesDialog from './CommoditiesDialog'

interface ISelector {
  carrier: ICarrierState
  carrierCommodities: ICommodityState
}

export const Commodities = () => {
  const {
    carrier: { carrierDetails },
    carrierCommodities: { commodities, error, loading },
  } = useSelector<IStore, ISelector>(({ carrier, carrierCommodities }) => ({
    carrier,
    carrierCommodities,
  }))
  const dispatch = useDispatch()
  useEffect(() => {
    if (carrierDetails?.id) dispatch(getCarrierCommodities(carrierDetails?.id))
  }, [carrierDetails?.id])
  const showDialog = () => {
    dispatch(showCommoditiesDialog())
  }

  const notVerifiedCommodities = commodities.filter(commodity => commodity.covered === null)

  const coveredOrExcluded = commodities.filter(commodity => commodity.covered !== null)
  const coveredCommodities = coveredOrExcluded.filter(commodity => commodity.covered)
  const excludedCommodities = coveredOrExcluded.filter(commodity => !commodity.covered)

  return (
    <Card>
      <div className='row no-gutter pv4 ml0'>
        <Typography variant='h3' className='fl mb0 mt2'>
          Commodities Covered by Insurance
        </Typography>
        <div className='fr'>
          <IconButton
            Icon={EditIcon}
            color='tertiary'
            onClick={showDialog}
            data-test='edit-commodity'
            label='Edit'
            size='large'
          />
        </div>
      </div>
      {loading && <Spinner position='relative' />}
      {error && <InlineMessage message={error} type={INLINE_MESSAGE_VARIANTS.ALERT} />}
      {!loading && !error && (
        <>
          {coveredCommodities.length > 0 && (
            <div className='pv2'>
              <span>Covered</span>
              {coveredCommodities.map(coveredCommodity => (
                <div key={coveredCommodity.id}>
                  <SuccessIcon /> {coveredCommodity.description}{' '}
                  <span className='text-secondary'>
                    Verified by {coveredCommodity.verifiedBy.name} on{' '}
                    {moment
                      .tz(coveredCommodity.verifiedBy.date, FORMATS.defaultTimeZone)
                      .format(FORMATS.dateFormat)}
                  </span>
                </div>
              ))}
            </div>
          )}
          {excludedCommodities.length > 0 && (
            <div className='pv2'>
              <span>Excluded</span>
              {excludedCommodities.map(coveredCommodity => (
                <div key={coveredCommodity.id}>
                  <AlertIcon /> {coveredCommodity.description}{' '}
                  <span className='text-secondary'>
                    Verified by {coveredCommodity.verifiedBy.name} on{' '}
                    {moment
                      .tz(coveredCommodity.verifiedBy.date, FORMATS.defaultTimeZone)
                      .format(FORMATS.dateFormat)}
                  </span>
                </div>
              ))}
            </div>
          )}
          {notVerifiedCommodities.length > 0 && (
            <div className='pv2'>
              <span>Not Verified</span>
              <div>
                {notVerifiedCommodities.map(commodity => commodity.description).join(' · ')}
              </div>
            </div>
          )}
        </>
      )}
      <CommoditiesDialog />
    </Card>
  )
}
